import ApiMethods from 'shared/api/api-methods';
import { queryOptions } from '@tanstack/react-query';
import { filesQueryKeys } from '@/enteties/file-storage/api/file-storage.query-key';
import type { Directory } from 'shared/types/file-storage-types';
import type { StatusResponse } from 'shared/api/services/types';
import { saveAs } from 'file-saver';
import axiosInstance from '@/shared/api/axios-instance';
import * as m from '@paraglide/messages';
import { paramsToQueryString } from '@/shared/lib/util';

const url = '/git';

const getGitToken = async (slug: string): Promise<{ token: string }> => {
  const res = await ApiMethods.get(`${url}/${slug}/files/download_token`);
  if (!res.ok) throw new Error(`Failed to get download token: ${res.status}`);
  return res.json();
};

const downloadGitFiles = async ({
  slug,
  branch,
  path,
  filename,
}: {
  slug: string;
  branch: string;
  path?: string;
  filename: string;
}) => {
  try {
    const tokenRes = await getGitToken(slug);

    if (!tokenRes?.token) {
      throw new Error(m.repo_download_files_error());
    }

    const params = {
      token: tokenRes.token,
      branch,
      path,
    };

    const res = await axiosInstance.get(
      `${url}/${slug}/files/download${paramsToQueryString(params)}`,
      {
        headers: {
          Accept: 'application/zip',
        },
        responseType: 'blob',
      }
    );

    saveAs(res.data, filename);
  } catch (error) {
    throw new Error(m.repo_download_files_error());
  }
};

const getCodeFiles = {
  async fetchCodeFiles(slug: string, branch: string, path: string) {
    const params = new URLSearchParams({
      branch,
      path,
    });

    const res = await ApiMethods.get(`${url}/${slug}/files?${params}`);
    if (!res.ok) return [];
    return await res.json();
  },
  getCodeFilesQueryOptions: ({
    slug,
    tab,
    branch,
    path,
  }: {
    slug: string;
    tab: Directory;
    branch: string;
    path: string;
  }) =>
    queryOptions({
      queryKey: filesQueryKeys.projectFilesGit(slug, tab, branch, path),
      queryFn: () => getCodeFiles.fetchCodeFiles(slug, branch, path),
    }),
};

async function deleteCodeFiles(
  slug: string,
  data: {
    commitMsg: string;
    email: string;
    branch: string;
    filePaths: string[];
  }
): Promise<StatusResponse> {
  const res = await ApiMethods.patch(`${url}/userdata/${slug}`, data);
  if (!res.ok) throw new Error(`${res.status}`);
  return { status: 'OK' };
}

export { deleteCodeFiles, downloadGitFiles, getCodeFiles, getGitToken };
